import { ApiError } from '@rp/client-core';
import type { MeView, StationBoardView, StationTicketView } from '@rp/contracts';
import { formatMinor, type TicketAction } from '@rp/domain';
import { useEffect, useState } from 'react';
import { navigate } from '../../infra/router';
import { api, signOut, topics } from '../../infra/session';
import { useFeed } from '../../infra/use-feed';
import { ConnectionDot, elapsed } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { type Notice, NoticeCenter, useNotices } from '../../ui/notifications';

const whereOf = (t: StationTicketView) =>
  t.channel === 'dine_in'
    ? t.tableLabel
      ? `Table ${t.tableLabel}`
      : t.areaName
    : `Takeaway${t.customerName ? ` · ${t.customerName}` : ''}`;
const STATE: Record<string, string> = {
  new: 'New order',
  accepted: 'Accepted',
  in_preparation: 'Preparing',
  on_hold: 'Paused',
  ready: 'Ready',
  cancelled: 'Voided',
};

/**
 * The ticket's clock: how long it has been in its current stage. NEW: waiting to be accepted (since
 * it arrived). ACCEPTED: since accepted. PREPARING / PAUSED: since START. READY: since ready.
 * "Late" (target time) still counts from arrival, as before.
 */
function stageClock(t: StationTicketView): { label: string; since: string } {
  if (t.status === 'accepted' && t.acceptedAt) return { label: 'Accepted', since: t.acceptedAt };
  if ((t.status === 'in_preparation' || t.status === 'on_hold') && t.startedAt)
    return { label: 'Preparing', since: t.startedAt };
  if (t.status === 'ready' && t.readyAt) return { label: 'Ready', since: t.readyAt };
  return { label: 'Waiting', since: t.createdAt };
}

const clock = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** What changed on this station since the last authoritative reload. */
/** Who voided a whole ticket and why (from its voided lines). */
function voidSummary(t: StationTicketView): string {
  const line = t.items.find((i) => i.voidedBy || i.voidReason);
  if (!line) return 'Cancelled';
  return [line.voidedBy ? `Voided by ${line.voidedBy}` : null, line.voidReason].filter(Boolean).join(' · ');
}

function kitchenDiff(
  before: StationBoardView,
  after: StationBoardView,
): Omit<Notice, 'id' | 'at' | 'read'>[] {
  const out: Omit<Notice, 'id' | 'at' | 'read'>[] = [];
  const prev = new Map(before.tickets.map((t) => [t.id, t]));
  const knownOrders = new Set(before.tickets.map((t) => t.orderNumber));
  for (const t of after.tickets) {
    const p = prev.get(t.id);
    const items = t.items.map((i) => `${i.quantity}× ${i.name}`).join(', ');
    if (!p && t.status === 'cancelled') {
      // Ended by a void before this screen saw it: say so, never "new order".
      out.push({ kind: 'voided', title: `#${t.orderNumber} · VOIDED`, detail: 'Stop preparation' });
      continue;
    }
    if (!p) {
      out.push({
        kind: knownOrders.has(t.orderNumber) ? 'added' : 'new',
        title: `#${t.orderNumber} · ${whereOf(t)}${t.isRush ? ' · RUSH' : ''}`,
        detail: items,
      });
      continue;
    }
    for (const i of t.items) {
      const was = p.items.find((x) => x.id === i.id);
      if (
        was &&
        was.status !== 'voided' &&
        was.status !== 'cancelled' &&
        (i.status === 'voided' || i.status === 'cancelled')
      )
        out.push({
          kind: 'voided',
          title: `VOID #${t.orderNumber} · ${i.quantity}× ${i.name}`,
          detail: `Stop preparation${i.voidReason ? ` · ${i.voidReason}` : ''}`,
        });
    }
    if (p.status === 'ready' && t.status !== 'ready' && t.status !== 'completed')
      out.push({ kind: 'recalled', title: `#${t.orderNumber} recalled`, detail: whereOf(t) });
    if (!p.isRush && t.isRush)
      out.push({ kind: 'delayed', title: `#${t.orderNumber} is now RUSH`, detail: whereOf(t) });
  }
  return out;
}

/**
 * Station screen. Shows only this station's tickets (enforced by the server: a paired KDS cannot
 * read or act on another station). Actions go through the API; the screen never writes data itself.
 */
export function KdsScreen({ me, stationParam }: { me: MeView; stationParam: string | null }) {
  const stationId = me.device?.stationId ?? stationParam;
  const branchId = me.device?.branchId ?? me.branches[0]?.id ?? '';
  if (!stationId) return <StationPicker me={me} branchId={branchId} />;
  return <Board me={me} stationId={stationId} branchId={branchId} isDevice={me.kind === 'device'} />;
}

function useStations(branchId: string, enabled: boolean) {
  const [stations, setStations] = useState<{ id: string; name: string }[] | null>(null);
  useEffect(() => {
    if (!enabled) return;
    api
      .configuration()
      .then((c) =>
        setStations(
          (c.stations as { id: string; name: string; branchId: string; isActive: boolean }[]).filter(
            (s) => s.branchId === branchId && s.isActive,
          ),
        ),
      )
      .catch(() => setStations([]));
  }, [branchId, enabled]);
  return stations;
}

function StationPicker({ me, branchId }: { me: MeView; branchId: string }) {
  const stations = useStations(branchId, true);
  return (
    <div className="kds">
      <div className="bar">
        <button type="button" className="dark-btn" onClick={() => navigate('/dashboard')} aria-label="Back">
          <Icon name="arrow-left" size={20} />
        </button>
        <img className="bar-logo" src="/logo-64.png" alt="" />
        <span className="title">Kitchen</span>
        <span className="count">Choose your station</span>
        <span className="grow" />
        <button type="button" className="dark-btn" onClick={() => navigate('/expo')}>
          Supervisor view
        </button>
        <span className="count">{me.displayName}</span>
      </div>
      <div className="station-grid">
        {stations?.map((s) => (
          <button
            key={s.id}
            type="button"
            className="station-tile"
            onClick={() => navigate(`/kds?station=${s.id}`)}
          >
            {s.name}
            <span>Open the {s.name} screen</span>
          </button>
        ))}
      </div>
      {stations && stations.length === 0 ? (
        <div className="kds-empty">No kitchen stations yet. Create them in Stations &amp; routing.</div>
      ) : null}
    </div>
  );
}

function Board({
  me,
  stationId,
  branchId,
  isDevice,
}: {
  me: MeView;
  stationId: string;
  branchId: string;
  isDevice: boolean;
}) {
  const feed = useFeed(`kds:${stationId}`, () => api.stationBoard(stationId), {
    topic: topics.station(branchId, stationId),
    pollMs: 20_000, // safety poll: a silently dead socket cannot hide tickets for long
  });
  const notices = useNotices(feed.data, kitchenDiff);
  const stations = useStations(branchId, !isDevice);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Heartbeat for paired screens, so managers can see this KDS is alive.
  useEffect(() => {
    if (!isDevice) return;
    const beat = () => void api.heartbeat({ appVersion: 'web-kds', printers: [] }).catch(() => undefined);
    beat();
    const t = setInterval(beat, 30_000);
    return () => clearInterval(t);
  }, [isDevice]);

  // Latest known status/version per ticket from our own actions, so a quick START -> READY
  // uses the new version immediately instead of waiting for the board to reload.
  const [local, setLocal] = useState<
    Record<string, { status: StationTicketView['status']; version: number }>
  >({});

  async function act(ticket: StationTicketView, action: TicketAction) {
    setBusy(ticket.id);
    setError(null);
    try {
      const send = () => api.ticketAction(ticket.id, { action, expectedVersion: ticket.version });
      // "Busy, try again" means nothing was saved: one automatic retry saves the cook a tap.
      const order = await send().catch((e) =>
        e instanceof ApiError && e.retryable && e.code !== 'NETWORK' ? send() : Promise.reject(e),
      );
      const updated = order.tickets.find((x) => x.id === ticket.id);
      if (updated)
        setLocal((m) => ({ ...m, [ticket.id]: { status: updated.status, version: updated.version } }));
    } catch (e) {
      const conflict = e instanceof ApiError && e.code === 'VERSION_CONFLICT';
      setError(
        conflict
          ? 'Updated on another screen: refreshed, tap again if needed'
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(null);
      feed.refresh();
    }
  }

  const board = feed.data
    ? {
        ...feed.data,
        tickets: feed.data.tickets
          .map((t) => {
            const mine = local[t.id];
            return mine && mine.version > t.version
              ? { ...t, status: mine.status, version: mine.version }
              : t;
          })
          // Voided (cancelled) tickets stay: the server keeps them 15 minutes as VOIDED, STOP PREPARATION.
          .filter((t) => t.status !== 'completed'),
      }
    : null;
  const target = board?.station.targetPrepSeconds ?? null;
  const money = (m: number) => formatMinor(m, board?.station.currency ?? 'GHS');
  return (
    <div className="kds">
      <div className="bar">
        {!isDevice ? (
          <button
            type="button"
            className="dark-btn"
            onClick={() => navigate('/kds')}
            aria-label="All stations"
          >
            <Icon name="arrow-left" size={20} />
          </button>
        ) : null}
        <img className="bar-logo" src="/logo-64.png" alt="" />
        <span className="title">{board?.station.name ?? 'Kitchen'}</span>
        <span className="count">{board ? `${board.tickets.length} open` : ''}</span>
        {!isDevice && stations && stations.length > 1 ? (
          <div className="seg dark">
            {stations.map((s) => (
              <button
                key={s.id}
                type="button"
                className={s.id === stationId ? 'on' : ''}
                onClick={() => navigate(`/kds?station=${s.id}`)}
              >
                {s.name}
              </button>
            ))}
          </div>
        ) : null}
        <span className="grow" />
        <NoticeCenter state={notices} />
        <ConnectionDot state={feed.connection} />
        {!isDevice ? (
          <button type="button" className="dark-btn" onClick={() => void signOut()}>
            Sign out
          </button>
        ) : (
          <span className="count">{me.device?.name}</span>
        )}
      </div>
      {board?.printerAlerts.map((a) => (
        <div key={a.printerId} className="alert">
          Printer problem: {a.printerName} — {a.deadJobs + a.failedJobs} ticket(s) not printed
          {a.lastError ? ` (${a.lastError})` : ''}. Tickets are still shown here.
        </div>
      ))}
      {error ? <div className="alert warn">{error}</div> : null}
      {feed.connection === 'polling' ? (
        <div className="alert warn">Kitchen connection lost. Reconnecting… (screen refreshes every 20 s)</div>
      ) : null}
      <div className="tickets">
        {board?.tickets.map((t) => {
          const stage = stageClock(t);
          const voided = t.status === 'cancelled';
          const late =
            target !== null &&
            t.status !== 'ready' &&
            !voided &&
            now - new Date(t.createdAt).getTime() > target * 1000;
          return (
            <article
              key={t.id}
              className={`ticket ${t.status} ${late ? 'late' : ''} ${t.isRush ? 'rush' : ''}`}
              aria-label={`Order ${t.orderNumber}${voided ? ', voided' : ''}`}
            >
              <header>
                <span className="num">#{t.orderNumber}</span>
                {voided ? null : (
                  <span className="elapsed" title={`${stage.label} for ${elapsed(stage.since, now)}`}>
                    <small>{stage.label}</small> {elapsed(stage.since, now)}
                  </span>
                )}
              </header>
              <div className="where">
                {t.channel === 'dine_in'
                  ? `${t.areaName}${t.tableLabel ? ` · Table ${t.tableLabel}` : ''}`
                  : `Takeaway${t.customerName ? ` · ${t.customerName}` : ''}`}
                <span className="t-status">
                  {late ? 'Late · ' : ''}
                  {STATE[t.status] ?? t.status} · {clock(t.sentAt ?? t.createdAt)}
                </span>
              </div>
              {voided ? (
                <div className="void-banner" role="alert">
                  <strong>VOIDED · STOP PREPARATION</strong>
                  <span>
                    {voidSummary(t)}
                    {t.cancelledAt ? ` · ${clock(t.cancelledAt)}` : ''}
                  </span>
                </div>
              ) : null}
              {t.sentBy ? (
                <div className="sent-by">
                  Sent by <strong>{t.sentBy.name}</strong>
                  {t.sentBy.role ? ` • ${t.sentBy.role}` : ''}
                </div>
              ) : null}
              {t.acceptedAt ? (
                <div className="sent-by">
                  Accepted{t.acceptedBy ? ' by ' : ''}
                  {t.acceptedBy ? <strong>{t.acceptedBy.name}</strong> : null}
                  {t.acceptedBy?.role ? ` • ${t.acceptedBy.role}` : ''}
                  {t.acceptedOn ? ' on ' : ''}
                  {t.acceptedOn ? <strong>{t.acceptedOn}</strong> : null} · {clock(t.acceptedAt)}
                </div>
              ) : null}
              <ul>
                {t.items.map((i) => (
                  <li
                    key={i.id}
                    className={i.status === 'voided' || i.status === 'cancelled' ? 'voided' : ''}
                  >
                    <span className="qtyx">{i.quantity} ×</span> <span className="iname">{i.name}</span>
                    {i.status === 'voided' || i.status === 'cancelled' ? (
                      <span className="void-tag">
                        VOID · STOP PREPARATION
                        {i.voidedBy || i.voidReason
                          ? ` · ${[i.voidedBy, i.voidReason].filter(Boolean).join(' · ')}`
                          : ''}
                      </span>
                    ) : null}
                    {i.lineTotal !== null ? <span className="price">{money(i.lineTotal)}</span> : null}
                    {i.modifiers.map((m) => (
                      <span key={m} className="mod">
                        + {m}
                      </span>
                    ))}
                    {i.unitPrice !== null && i.grossTotal !== null ? (
                      <span className="price-calc">
                        {i.quantity} × {money(i.unitPrice)}
                        {i.grossTotal !== i.quantity * i.unitPrice ? ' + extras' : ''} = {money(i.grossTotal)}
                        {i.promotionName && i.promotionDiscount ? (
                          <>
                            {' '}
                            · {i.promotionName} −{money(i.promotionDiscount)}
                          </>
                        ) : null}
                        {i.manualDiscount ? <> · Discount −{money(i.manualDiscount)}</> : null}
                      </span>
                    ) : null}
                    {i.notes ? <span className="note">! {i.notes}</span> : null}
                  </li>
                ))}
              </ul>
              {board.station.showPrices ? (
                <div className="ticket-total">
                  <span>Total</span>
                  <span>
                    {money(
                      t.items
                        .filter((i) => i.status !== 'voided' && i.status !== 'cancelled')
                        .reduce((a, i) => a + (i.lineTotal ?? 0), 0),
                    )}
                  </span>
                </div>
              ) : null}
              {t.orderNotes ? <div className="order-note">Note: {t.orderNotes}</div> : null}
              {voided ? (
                <div className="void-foot">Leaves this screen 15 minutes after the void.</div>
              ) : (
                <div className="actions">
                  {t.status === 'new' ? (
                    <button
                      type="button"
                      className="kbtn-accept"
                      disabled={busy === t.id}
                      onClick={() => void act(t, 'accept')}
                    >
                      ACCEPT
                    </button>
                  ) : null}
                  {t.status === 'accepted' ? (
                    <button
                      type="button"
                      className="kbtn-start"
                      disabled={busy === t.id}
                      onClick={() => void act(t, 'start')}
                    >
                      START
                    </button>
                  ) : null}
                  {t.status === 'on_hold' ? (
                    <button
                      type="button"
                      className="kbtn-start"
                      disabled={busy === t.id}
                      onClick={() => void act(t, 'resume')}
                    >
                      RESUME
                    </button>
                  ) : null}
                  {t.status === 'in_preparation' ? (
                    <button
                      type="button"
                      className="kbtn-minor"
                      disabled={busy === t.id}
                      onClick={() => void act(t, 'pause')}
                    >
                      PAUSE
                    </button>
                  ) : null}
                  {t.status === 'in_preparation' || t.status === 'on_hold' ? (
                    <button
                      type="button"
                      className="kbtn-ready"
                      disabled={busy === t.id}
                      onClick={() => void act(t, 'ready')}
                    >
                      READY
                    </button>
                  ) : null}
                  {t.status === 'ready' ? (
                    <>
                      <button
                        type="button"
                        className="kbtn-minor"
                        disabled={busy === t.id}
                        onClick={() => void act(t, 'recall')}
                      >
                        RECALL
                      </button>
                      <button
                        type="button"
                        className="kbtn-bump"
                        disabled={busy === t.id}
                        onClick={() => void act(t, 'complete')}
                      >
                        DONE
                      </button>
                    </>
                  ) : null}
                </div>
              )}
            </article>
          );
        })}
      </div>
      {board && board.tickets.length === 0 ? (
        <div className="kds-empty">
          <img className="empty-logo" src="/logo-192.png" alt="" />
          <strong>Kitchen is clear</strong>
          <span>No active orders are waiting. New orders appear here the moment they are sent.</span>
        </div>
      ) : null}
    </div>
  );
}
